import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  UserX, Filter, Search, ChevronDown, Calendar,
  CheckCircle, XCircle, Clock, Users, RefreshCw, Plus, Trash2
} from "lucide-react";
import { Link } from "react-router-dom";
import { handleGet, handleRemove } from "../../../controllers/controller.all";
import { Send } from "../../../services/service.api";
import { toast } from "react-toastify";
import { confirmAlert } from "../../../components/ui/confirmAlert";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";

const STATUSES = [
  { value: "EN COURS", label: "En cours", icon: Clock, color: "text-amber-600", bg: "bg-amber-50  border-amber-200" },
  { value: "JUSTIFIE", label: "Justifiée", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  { value: "NON JUSTIFIE", label: "Non justifiée", icon: XCircle, color: "text-red-600", bg: "bg-red-50    border-red-200" },
];

const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP["EN COURS"];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.bg} ${s.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {s.label}
    </span>
  );
}

function StatusDropdown({ absence, token, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 'auto', bottom: 'auto', right: 0 });
  const btnRef = useRef(null);

  const handleSelect = async (newStatus) => {
    if (newStatus === absence.status) { setOpen(false); return; }
    setSaving(true);
    setOpen(false);
    try {
      const res = await Send(`/absence/${absence._id}/status`, { status: newStatus }, true, token);
      if (res?.state === "success") {
        toast.success("Statut mis à jour ✓");
        onUpdated(absence._id, newStatus);
      } else {
        toast.warning(res?.message || "Aucune modification");
      }
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative inline-block text-left">
      <button
        ref={btnRef}
        onClick={() => {
          if (!open) {
            const rect = btnRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const menuHeight = 150; // approximate height for the menu items

            if (spaceBelow < menuHeight) {
              // Show above if not enough space below
              setMenuPos({
                bottom: window.innerHeight - rect.top + 6,
                top: 'auto',
                right: window.innerWidth - rect.right
              });
            } else {
              // Show below
              setMenuPos({
                top: rect.bottom + 6,
                bottom: 'auto',
                right: window.innerWidth - rect.right
              });
            }
          }
          setOpen((v) => !v);
        }}
        disabled={saving}
        title="Changer le statut"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 shadow-sm"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${saving ? "animate-spin" : ""}`} />
        {saving ? "Saving…" : "Changer"}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          {/* Click-away overlay */}
          <div className="fixed inset-0 z-[999]" onClick={() => setOpen(false)} />
          {/* Menu — fixed so it escapes overflow:auto clipping */}
          <div
            className="fixed z-[1000] w-44 rounded-xl border border-gray-100 bg-white shadow-xl overflow-hidden"
            style={{
              top: menuPos.top !== 'auto' ? menuPos.top : undefined,
              bottom: menuPos.bottom !== 'auto' ? menuPos.bottom : undefined,
              right: menuPos.right
            }}
          >
            {STATUSES.map((s) => {
              const Icon = s.icon;
              const isCurrent = s.value === absence.status;
              return (
                <button
                  key={s.value}
                  onClick={() => handleSelect(s.value)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors text-left ${isCurrent
                      ? "bg-indigo-50 text-indigo-700 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isCurrent ? "text-indigo-500" : s.color}`} />
                  {s.label}
                  {isCurrent && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-center gap-3 sm:gap-4 shadow-sm min-w-0">
      <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">{value}</p>
        <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">{label}</p>
      </div>
    </div>
  );
}

export default function AbsenceList() {
  const user = useSelector((s) => s.userReducer?.user);
  const token = user?.access_token || "";
  const ecoleId = user?.ecole?._id || user?.ecole;

  const [classes, setClasses] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedClasse, setSelectedClasse] = useState("");
  const [selectedAnnee, setSelectedAnnee] = useState("");
  const [searchEleve, setSearchEleve] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [annees, setAnnees] = useState([]);

  // Pagination & Sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const itemsPerPage = 20;

  // Load classes, years, and subjects for the school
  useEffect(() => {
    if (!ecoleId || !token) return;
    handleGet(`/classe/ecole/${ecoleId}`, (res) => {
      setClasses(res?.classes || []);
    }, token);
    handleGet(`/ecole-annee/ecole/${ecoleId}`, (res) => {
      setAnnees(res?.annees || []);
    }, token);
    handleGet(`/matiere/ecole/${ecoleId}`, (res) => {
      setMatieres(res?.matieres || []);
    }, token);
  }, [ecoleId, token]);

  useEffect(() => {
    if (!selectedClasse || !token) { setAbsences([]); return; }
    setLoading(true);
    let queryParams = `?skip=0&limit=100000`;
    if (selectedAnnee) queryParams += `&annee=${selectedAnnee}`;
    
    handleGet(`/absence/classe/${selectedClasse}${queryParams}`, (res) => {
      setAbsences(res?.absences || []);
      setLoading(false);
    }, token);
  }, [selectedClasse, selectedAnnee, token]);
  const handleSort = (key, order) => {
      setSortKey(key);
      setSortOrder(order);
  };

  // Optimistic update after status change
  const handleStatusUpdated = (absenceId, newStatus) => {
    setAbsences((prev) =>
      prev.map((a) => a._id === absenceId ? { ...a, status: newStatus } : a)
    );
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirmAlert({
      title: "Supprimer l'absence",
      message: "Êtes-vous sûr de vouloir supprimer cette absence ? Cette action est irréversible.",
      confirmText: "Oui, supprimer",
      cancelText: "Annuler",
      type: "danger"
    });

    if (isConfirmed) {
      await handleRemove(`/absence/${id}`, () => {
        setAbsences(absences.filter(a => a._id !== id));
      }, token);
    }
  };

  // Stats
  const stats = useMemo(() => ({
    total: absences.length,
    justifiees: absences.filter((a) => a.status === "JUSTIFIE").length,
    nonJust: absences.filter((a) => a.status === "NON JUSTIFIE").length,
    enCours: absences.filter((a) => a.status === "EN COURS").length,
  }), [absences]);

  const filtered = useMemo(() => {
    return absences.filter((a) => {
      const eleveName = typeof a.eleve === "object"
        ? `${a.eleve?.prenom || ""} ${a.eleve?.nom || ""}`.toLowerCase()
        : (a.eleve || "").toLowerCase();
      const matchEleve = !searchEleve || eleveName.includes(searchEleve.toLowerCase());
      const matchStatus = !filterStatus || a.status === filterStatus;
      const matchDate = !filterDate || (a.date || "").startsWith(filterDate);
      return matchEleve && matchStatus && matchDate;
    });
  }, [absences, searchEleve, filterStatus, filterDate]);

  const sortedAbsences = [...filtered].sort((a, b) => {
      let valA = a[sortKey] || "";
      let valB = b[sortKey] || "";
      
      if (typeof valA === 'object' && valA !== null) {
          valA = valA.libelle || valA.nom || valA.prenom || "";
      }
      if (typeof valB === 'object' && valB !== null) {
          valB = valB.libelle || valB.nom || valB.prenom || "";
      }

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
  });

  const paginatedAbsences = sortedAbsences.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const classeLabel = useMemo(() => {
    const c = classes.find((c) => (c._id || c) === selectedClasse);
    return c?.libelle || c?.nom || c?.name || "";
  }, [classes, selectedClasse]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return dateStr; }
  };

  const getEleveName = (eleve) => {
    if (typeof eleve === "object" && eleve !== null)
      return `${eleve.prenom || ""} ${eleve.nom || ""}`.trim() || eleve.name || "—";
    return eleve || "—";
  };

  const getMatiereName = (matiere) => {
    if (!matiere) return "—";
    if (typeof matiere === "object" && matiere !== null) {
      return matiere.libelle || matiere.nom || matiere.name || "—";
    }
    const found = matieres.find((m) => (m._id || m) === matiere);
    if (found) return found.libelle || found.nom || found.name || "—";
    return matiere || "—";
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <header className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Absences par classe</h1>
          <p className="text-gray-500">Sélectionnez une classe pour voir, filtrer et modifier les absences.</p>
        </header>
        <Link
          to="/absences/create"
          className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
        >
          <Plus className="h-4 w-4" /> Nouvelle Absence
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex flex-wrap gap-3 items-end">

          {/* Class selector */}
          <div className="flex flex-col gap-1.5 min-w-[200px]">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Classe
            </label>
            <div className="relative">
              <select
                value={selectedClasse}
                onChange={(e) => { setSelectedClasse(e.target.value); setSearchEleve(""); setFilterStatus(""); setFilterDate(""); }}
                className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 pr-9 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">— Choisir une classe —</option>
                {classes.map((c) => (
                  <option key={c._id || c} value={c._id || c}>{c.libelle}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* Annee selector */}
          <div className="flex flex-col gap-1.5 min-w-[150px]">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Année
            </label>
            <div className="relative">
              <select
                value={selectedAnnee}
                onChange={(e) => setSelectedAnnee(e.target.value)}
                disabled={!selectedClasse}
                className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 pr-9 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="">Année Active</option>
                {annees.map((a) => (
                  <option key={a._id} value={a.annee}>{a.annee}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* Student search */}
          <div className="flex flex-col gap-1.5 min-w-[200px] flex-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5" /> Élève
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un élève…"
                value={searchEleve}
                onChange={(e) => setSearchEleve(e.target.value)}
                disabled={!selectedClasse}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="flex flex-col gap-1.5 min-w-[170px]">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5" /> Statut
            </label>
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                disabled={!selectedClasse}
                className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 pr-9 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="">Tous les statuts</option>
                <option value="EN COURS">En cours</option>
                <option value="JUSTIFIE">Justifiée</option>
                <option value="NON JUSTIFIE">Non justifiée</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* Date filter */}
          <div className="flex flex-col gap-1.5 min-w-[170px]">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Date
            </label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              disabled={!selectedClasse}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          {/* Reset filters */}
          {(searchEleve || filterStatus || filterDate) && (
            <button
              onClick={() => { setSearchEleve(""); setFilterStatus(""); setFilterDate(""); }}
              className="self-end px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors border border-gray-200"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {selectedClasse && !loading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={<UserX className="w-5 h-5 text-gray-600" />} label="Total absences" value={stats.total} color="bg-gray-100" />
          <StatCard icon={<Clock className="w-5 h-5 text-amber-600" />} label="En cours" value={stats.enCours} color="bg-amber-50" />
          <StatCard icon={<CheckCircle className="w-5 h-5 text-emerald-600" />} label="Justifiées" value={stats.justifiees} color="bg-emerald-50" />
          <StatCard icon={<XCircle className="w-5 h-5 text-red-600" />} label="Non justifiées" value={stats.nonJust} color="bg-red-50" />
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">
            {selectedClasse ? `Absences${classeLabel ? ` — ${classeLabel}` : ""}` : "Sélectionnez une classe"}
          </h2>
          {selectedClasse && !loading && (
            <span className="text-sm text-gray-400">
              {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <div className="w-6 h-6 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin mr-3" />
            Chargement…
          </div>
        )}

        {/* No class */}
        {!loading && !selectedClasse && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
            <Users className="w-12 h-12 text-gray-200" />
            <p className="text-sm">Choisissez une classe pour afficher les absences</p>
          </div>
        )}

        {/* Empty */}
        {!loading && selectedClasse && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
            <UserX className="w-12 h-12 text-gray-200" />
            <p className="text-sm">Aucune absence trouvée pour ces critères</p>
          </div>
        )}

        {/* Data */}
        {!loading && selectedClasse && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/70">
                  <SortableHeader label="Élève" sortKey="eleve" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Matière" sortKey="matiere" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Date" sortKey="date" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Statut" sortKey="status" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Remarques</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginatedAbsences.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-sm text-gray-500">
                      Aucune absence trouvée.
                    </td>
                  </tr>
                ) : (
                  paginatedAbsences.map((absence, idx) => (
                    <tr key={absence._id || idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-700 font-semibold text-xs flex-shrink-0">
                            {getEleveName(absence.eleve).charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900">{getEleveName(absence.eleve)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{getMatiereName(absence.matiere)}</td>
                      <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{formatDate(absence.date)}</td>
                      <td className="px-6 py-4">
                        <StatusBadge status={absence.status} />
                      </td>
                      <td className="px-6 py-4 text-gray-500 max-w-xs truncate">
                        {absence.remarques || <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <StatusDropdown
                            absence={absence}
                            token={token}
                            onUpdated={handleStatusUpdated}
                          />
                          <button
                            onClick={() => handleDelete(absence._id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Supprimer l'absence"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {!loading && selectedClasse && filtered.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}
